import * as Yup from 'yup';
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FilledButton,
  MAIN_ROUTE,
  OutlinedButton,
  showError,
  Loader
} from 'shared';

import { useFormik } from 'formik';
import { toast } from 'react-toastify';

import { userStore } from 'entities/user';
import {
  EIntegrationSettingsType,
  ITavusPersona,
  ITavusPersonaFormik,
  tavusInitialPersona,
  tavusPersonaStore,
  timerConfigValidationSchema
} from 'entities/tavus/persona';
import { replicaStore } from 'entities/tavus/replica/model/replicaStore';
import { TopBar } from 'widgets/avatar-edit-persona-layout/ui/TopBar';
import { AvatarEditPersonaSidebar } from 'widgets/avatar-edit-persona-layout';
import { Preview } from 'widgets/avatar-edit-persona-layout/ui/Preview';
import { AvatarIdentityForm } from 'widgets/avatar-identity-form';

export const TavusCreatePersonaPage = () => {
  const { user } = userStore();

  const isPersonaCreating = tavusPersonaStore(
    (state) => state.isPersonaCreating
  );
  const addPersona = tavusPersonaStore((state) => state.addPersona);

  const { replicas, currentReplica, fetchReplicas } = replicaStore((state) => ({
    currentReplica: state.currentReplica,
    replicas: state.replicas,
    replicasLoading: state.replicasLoading,
    getReplicaById: state.getReplicaById,
    fetchReplicas: state.fetchReplicas
  }));
  const { fetchPersonas } = tavusPersonaStore((state) => ({
    fetchPersonas: state.fetchPersonas,
    tavusPersonasLoading: state.tavusPersonasLoading
  }));

  useEffect(() => {
    fetchReplicas();
    fetchPersonas();
  }, []);
  const { currentPersona } = tavusPersonaStore((state) => ({
    fetchOne: state.fetchOne,
    currentPersona: state.currentPersona,
    setCurrentPersona: state.setCurrentPersona
  }));

  const nav = useNavigate();

  const formik = useFormik<ITavusPersonaFormik>({
    initialValues: tavusInitialPersona,
    validationSchema: Yup.object({
      name: Yup.string(),
      systemPrompt: Yup.string()
        .required('Please enter description')
        .max(80, 'Please make description shorter'),
      customGreeting: Yup.string().max(500, 'Please make greeting shorter'),
      timerConfig: timerConfigValidationSchema,
      ambientAwarenessQueries: Yup.array().of(Yup.string()),
      preconversationConfig: Yup.object({
        context: Yup.string(),
        fields: Yup.array().of(
          Yup.object().shape({
            name: Yup.string()
              .matches(/^[A-Za-zА-Яа-я_]+$/, 'No numbers or spaces allowed')
              .notOneOf(['language'], 'This name is reserved')
              .required('Required')
          })
        )
      })
    }),
    onSubmit: async (values) => {
      const submitValues = { ...values };
      if (submitValues.name === '') {
        submitValues.name = 'My Video Agent';
      }

      const persona = await addPersona({
        persona_name: submitValues.name,
        context: submitValues.context,
        system_prompt: submitValues.systemPrompt,
        custom_background: submitValues.custom_background || '',
        custom_greeting: submitValues.customGreeting,
        default_replica_id: submitValues.defaultReplicaId,
        model: submitValues.model,
        timer_config:
          JSON.stringify(submitValues.timer_config) ||
          JSON.stringify({ duration: 0, events: [] }),
        preconversation_config: submitValues.preconversationConfig,
        voice_id: submitValues.voiceId,
        default_language: submitValues.defaultLanguage,
        perception_model: submitValues.perceptionModel,
        timer_config_enabled: false,
        ambient_awareness_queries: submitValues.ambientAwarenessQueries.filter(
          (item) => item?.trim()
        ),
        isRecording: false,
        embed: {
          cta_primary_button_link: '',
          second_button_text: 'Learn more',
          dark_mode: false,
          video_agent_name: 'Video agent',
          is_form_enabled: false,
          description: 'Ready to Meet Your AI Assistant?',
          button_text: 'Start call',
          powered_by: false,
          main_color: '#C34DFF',
          more_personalised_colors: true,
          font: 'Inter',
          camera: true,
          language_selection: true,
          captions_by_default: true,
          domains: [],
          type: EIntegrationSettingsType.EMBED,
          api_key_id: '',
          screen_share: false,
          cta_secondary_button_link: '',
          cta_primary_button_text: 'Start call',
          cta_secondary_button_text: 'Learn more',
          video_call_ended_custom_message:
            'Sign up and use Yepic conversational video APIs free.'
        },
        widget: {
          cta_primary_button_link: '',
          dark_mode: false,
          second_button_text: 'Learn more',
          bubble_background_color: '#C34DFF',
          bubble_icon_color: '#ffffff',
          bubble_icon_custom: false,
          bubble_icon_side: 'left',
          bubble_icon_url: '',
          bubble_icon_variant: 'first',
          custom_widget_trigger: '',
          welcome_button_color: '#C34DFF',
          video_agent_name: 'Video agent',
          is_form_enabled: false,
          description: 'Ready to Meet Your AI Assistant?',
          button_text: 'Start call',
          powered_by: false,
          main_color: '#C34DFF',
          more_personalised_colors: true,
          font: 'Inter',
          camera: true,
          language_selection: true,
          captions_by_default: true,
          domains: [],
          type: EIntegrationSettingsType.WIDGET,
          api_key_id: '',
          screen_share: false,
          cta_secondary_button_link: '',
          cta_primary_button_text: 'Start call',
          cta_secondary_button_text: 'Learn more',
          video_call_ended_custom_message:
            'Sign up and use Yepic conversational video APIs free.'
        }
      });

      if (persona) nav(MAIN_ROUTE + '?posthog-agents-create=true');
    }
  });

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      const displayErrors = (errObj: any, path: string[] = []) => {
        Object.entries(errObj).forEach(([key, val]) => {
          if (typeof val === 'string') {
            toast.error(`${val}`);
          } else if (typeof val === 'object') {
            displayErrors(val, [...path, key]);
          }
        });
      };

      displayErrors(errors);

      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => {
          acc[key] = true;
          return acc;
        }, {} as Record<string, boolean>)
      );

      return;
    }

    formik.handleSubmit(e);
  };

  useEffect(() => {
    if (replicas.length) {
      formik.setFieldValue('defaultReplicaId', replicas[0].replica_id);
    }
  }, [replicas]);

  useEffect(() => {
    if (!user) {
      showError('You need to login to access this page.');
      nav(MAIN_ROUTE);
    }
  }, [user]);

  useEffect(() => {
    if (currentReplica?.replica_id !== formik.values.defaultReplicaId) {
      formik.setFieldValue('defaultReplicaId', currentReplica?.replica_id);
    }
  }, [currentReplica]);

  const isDisabled = !formik.dirty || formik.isSubmitting;

  return (
    <form onSubmit={handleFormSubmit}>
      <div
        className={`ag-grid
      ag-grid-cols-1 ag-overflow-y-auto lg:ag-grid-rows-[0.5rem,calc(100vh-4rem)] lg:ag-grid-cols-[644px,calc(100vw-644px)] ag-h-screen ag-w-full`}
      >
        <div className="ag-col-span-full">
          <TopBar
            isDisabled={isDisabled}
            isPersonaCreating={isPersonaCreating}
          />
        </div>
        <AvatarEditPersonaSidebar
          IdentityFormComponent={AvatarIdentityForm}
          formik={formik}
          persona={currentPersona as ITavusPersona}
        />

        <Preview currentReplica={currentReplica} formik={formik} />
      </div>
    </form>
  );
};
